import {
  alpha,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Iconify from "../iconify/Iconify";
import TrainerSummaryView from "./TrainerSummaryView";

export default function TrainerAdminSummaryView({
  summary,
  error,
  deletingEntryKey,
  isTrainerListLoading = false,
  trainerOptions,
  selectedTrainerId,
  onSelectTrainer,
  onNavigate,
  onToday,
  onDeleteEntry,
  onShowPlan,
}) {
  const theme = useTheme();
  const selectedTrainer =
    trainerOptions.find((trainer) => trainer.id === selectedTrainerId) || null;
  const hasTrainerOptions = trainerOptions.length > 0;

  return (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          borderColor: alpha(theme.palette.primary.main, 0.18),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: { xs: 1.75, md: 2.25 },
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.07,
            )}, ${alpha(theme.palette.primary.main, 0.015)})`,
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Trainerübersicht
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                  Trainer auswählen und gebuchte Trainings für die Abrechnung im
                  Monat prüfen.
                </Typography>
              </Box>

              {selectedTrainer ? (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={`${selectedTrainer.bookingCount} Buchungen`}
                    sx={{
                      fontWeight: 700,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                    }}
                  />
                  <Chip
                    size="small"
                    label={`${selectedTrainer.siteCount} Standorte`}
                    sx={{
                      fontWeight: 700,
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      color: "warning.dark",
                    }}
                  />
                </Stack>
              ) : null}
            </Stack>

            <Autocomplete
              options={trainerOptions}
              disableClearable={hasTrainerOptions}
              value={selectedTrainer}
              onChange={(_, option) => onSelectTrainer(option?.id || "")}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option?.label || ""}
              noOptionsText="Keine Trainer verfügbar"
              sx={{ maxWidth: 560 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Trainer auswählen"
                  placeholder="Trainer suchen"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Stack spacing={0.1}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {`${option.bookingCount} Buchungen · ${option.siteCount} Standorte`}
                    </Typography>
                  </Stack>
                </Box>
              )}
            />

            {!hasTrainerOptions ? (
              <Stack direction="row" spacing={1} alignItems="center">
                {isTrainerListLoading ? (
                  <CircularProgress size={18} thickness={5} />
                ) : (
                  <Iconify
                    icon="mdi:information-outline"
                    width={18}
                    sx={{ color: "text.secondary" }}
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  {isTrainerListLoading
                    ? "Trainerliste wird geladen."
                    : "Aktuell sind keine Trainer mit Buchungen oder Standortzuordnung verfügbar."}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </Box>
      </Paper>

      {hasTrainerOptions && selectedTrainer ? (
        <TrainerSummaryView
          summary={summary}
          error={error}
          deletingEntryKey={deletingEntryKey}
          onNavigate={onNavigate}
          onToday={onToday}
          onDeleteEntry={onDeleteEntry}
          onShowPlan={onShowPlan}
        />
      ) : null}
    </Stack>
  );
}
